import { useState } from 'react';
import kit from './kit.module.css';
import styles from './GracefulFailureDemo.module.css';

type State = 'trigger' | 'success' | 'failure';

const NEXT_STEPS = [
  { label: 'Add work history manually', kind: kit.btnPrimary, reply: 'Opening the work history form. Your other details are saved.' },
  { label: 'Generate with available information', kind: kit.btnEdit, reply: "Generating a partial CV. Missing sections will be clearly marked so you can fill them later." },
  { label: 'Contact support', kind: kit.btnGhost, reply: 'Connecting you to support with this case attached, so you don\'t have to explain it again.' },
];

export function GracefulFailureDemo() {
  const [state, setState] = useState<State>('trigger');
  const [choice, setChoice] = useState<string | null>(null);

  return (
    <div className={kit.root}>
      <div className={kit.message}>Generate my CV from what I've entered so far.</div>

      {state === 'trigger' && (
        <>
          <div className={kit.agent}>
            <span className={kit.agentIcon} aria-hidden="true">✦</span>
            <p>Working on your CV...</p>
          </div>
          <p className={kit.feedback}>Pick an outcome to see how the agent responds.</p>
          <div className={kit.row}>
            <button type="button" className={`${kit.btn} ${kit.btnPrimary}`} onClick={() => setState('success')}>
              Simulate success
            </button>
            <button type="button" className={`${kit.btn} ${kit.btnEdit}`} onClick={() => setState('failure')}>
              Simulate failure
            </button>
          </div>
        </>
      )}

      {state === 'success' && (
        <>
          <div className={kit.agent}>
            <span className={kit.agentIcon} aria-hidden="true">✦</span>
            <p>Your CV is ready to review and download.</p>
          </div>
          <div className={kit.proposal}>
            <div className={kit.proposalTop}>
              <span>Complete</span>
              <span>CV draft</span>
            </div>
            <h3 className={kit.proposalTitle}>CV generated successfully</h3>
            <span className={`${kit.chip} ${kit.chipGood}`}>✓ All sections filled</span>
          </div>
          <p className={kit.feedback}>Use Reset to try the failure path.</p>
        </>
      )}

      {state === 'failure' && (
        <>
          <div className={kit.agent}>
            <span className={kit.agentIcon} aria-hidden="true">✦</span>
            <p>I couldn't complete this CV. Here's why, and how we can still get there.</p>
          </div>
          <div className={`${kit.proposal} ${styles.failure}`}>
            <div className={kit.proposalTop}>
              <span>Couldn't complete</span>
              <span className={`${kit.chip} ${kit.chipBad} ${styles.topChip}`}>Missing info</span>
            </div>
            <h3 className={kit.proposalTitle}>Some required information is missing</h3>
            <dl className={kit.fields}>
              <dt>Needed</dt>
              <dd>A job title and at least one work history entry</dd>
              <dt>Why</dt>
              <dd>Without these, the agent can't produce a meaningful result.</dd>
            </dl>
            <hr className={`${kit.divider} ${styles.gap}`} />
            <p className={kit.label}>WHAT WOULD YOU LIKE TO DO?</p>
            <div className={`${kit.row} ${styles.options}`}>
              {NEXT_STEPS.map((step) => (
                <button
                  key={step.label}
                  type="button"
                  className={`${kit.btn} ${step.kind}`}
                  onClick={() => setChoice(step.reply)}
                >
                  {step.label}
                </button>
              ))}
            </div>
          </div>
          <p className={kit.feedback} aria-live="polite">
            {choice ?? 'A clear reason and three ways forward - never a dead end.'}
          </p>
        </>
      )}
    </div>
  );
}
